// v186 admin banners API client.
//
// Reads the full list (admin) via GET /api/admin/banners; the
// currently-active row is also folded into /api/public-config so
// the AppShell can render it on every page without an admin
// roundtrip.

import useSWR from 'swr';
import type { ActiveBanner } from './publicConfig';

export type BannerSeverity = 'info' | 'warning' | 'critical';

export interface Banner extends ActiveBanner {
  created_by?: string;
}

interface BannersResponse {
  banners: Banner[];
}

const json = (url: string) =>
  fetch(url, { credentials: 'include' }).then((r) => {
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
    return r.json();
  });

export function useBanners() {
  return useSWR<BannersResponse>('/api/admin/banners', json);
}

export async function createBanner(input: {
  title: string;
  body?: string;
  severity?: BannerSeverity;
  starts_at: string;     // RFC3339
  ends_at: string;       // RFC3339
  dismissible?: boolean;
}): Promise<Banner> {
  const res = await fetch('/api/admin/banners', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  return res.json();
}

export async function deleteBanner(id: string): Promise<void> {
  const res = await fetch(`/api/admin/banners/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok && res.status !== 204) {
    throw new Error(`${res.status}: ${await res.text()}`);
  }
}

// Classify a banner row into one of three lifecycle states. The
// admin UI uses this to bucket the list into Active / Scheduled /
// Past sections.
export type BannerLifecycle = 'active' | 'scheduled' | 'past';

export function bannerLifecycle(b: Banner, now: Date = new Date()): BannerLifecycle {
  const start = new Date(b.starts_at);
  const end = new Date(b.ends_at);
  if (now >= end) return 'past';
  if (now < start) return 'scheduled';
  return 'active';
}
